import { Building2, ShieldCheck, Percent, Quote } from "lucide-react";

const COMPARE = [
  { name: "Sole proprietor", note: "Easiest to start, but ZERO protection — you and the business are legally the same. One claim can reach your home and savings." },
  { name: "LLC", note: "Liability protection + simple pass-through taxes. The usual starting point for owner-operators.", highlight: true },
  { name: "S-Corp election", note: "An LLC can later elect S-corp status to cut self-employment tax once your profit is higher — ask a CPA." },
];

// Plain-English explainer for what an LLC is (guide Step/Mission 1).
export default function LlcExplainer() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8" data-testid="llc-explainer">
      <div className="flex items-center gap-2 mb-3">
        <Building2 className="w-4 h-4 text-brand" />
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Plain-English explainer</span>
      </div>
      <h3 className="font-heading text-2xl font-black tracking-tight text-slate-900">What is an LLC?</h3>
      <p className="mt-3 text-slate-700 leading-relaxed">
        LLC stands for <strong>Limited Liability Company</strong>. It's a business structure you register with your state
        that makes your trucking operation its own legal "person" — separate from you as an individual.
      </p>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">Limited liability</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            If the business is sued or owes money, creditors can generally only go after the <strong>business's</strong>
            {" "}assets — not your house, personal savings or personal vehicle — as long as you keep business and personal
            money strictly separate.
          </p>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Percent className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">Pass-through taxes</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            An LLC usually doesn't pay federal income tax itself. Profits "pass through" to your personal tax return, so
            you avoid the double taxation a C-corporation faces. Less paperwork, too — no board or shareholder meetings.
          </p>
        </div>
      </div>

      <div className="mt-5">
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500 mb-3">How it compares</div>
        <div className="space-y-2">
          {COMPARE.map((c) => (
            <div
              key={c.name}
              data-testid={`llc-compare-${c.name.toLowerCase().replace(/\s|-/g, "-")}`}
              className={`rounded-xl border p-4 ${c.highlight ? "border-brand bg-orange-50/50 ring-1 ring-brand/20" : "border-slate-200 bg-white"}`}
            >
              <div className="text-sm font-bold text-slate-900">{c.name}</div>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">{c.note}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 rounded-xl bg-brand/5 border border-brand/20 p-5">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-7 h-7 rounded-full bg-brand flex items-center justify-center shrink-0">
            <Quote className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">Mentor tip</span>
        </div>
        <p className="text-sm text-slate-800 leading-relaxed">
          Most owner-operators start as a <strong>single-member LLC</strong> (just you). Open a separate business bank
          account the day it's formed and never mix personal and business money — that separation is what keeps your
          liability shield intact. This isn't legal or tax advice; confirm your setup with a CPA or attorney.
        </p>
      </div>
    </div>
  );
}
